import { SubscriptionTier } from "src/data/landing/queries/subscriptionTiers";

/**
 * The span a payment buys — the one money-shaped choice the restaurateur gets.
 * It picks both the number of months the subscription is extended by and the
 * per-month price read from the catalog; the amount itself is still derived
 * server-side and never sent.
 */
export type BillingPeriod = "MONTHLY" | "ANNUAL";

export const BILLING_PERIODS: BillingPeriod[] = ["MONTHLY", "ANNUAL"];

/** Months a payment for this period extends the subscription by. */
export const monthsIn = (period: BillingPeriod): number =>
  period === "ANNUAL" ? 12 : 1;

/** The per-month rate advertised for this period. */
export const monthlyRateFor = (
  tier: SubscriptionTier,
  period: BillingPeriod,
): number => (period === "ANNUAL" ? tier.annualMonthlyPrice : tier.monthlyPrice);

/**
 * What the transfer comes to: one month at the monthly price, or the whole year
 * at the discounted rate. Mirrors the backend's `SubscriptionTier.priceFor`, so
 * the figure shown before declaring matches the one snapshotted on the payment.
 */
export const priceFor = (
  tier: SubscriptionTier,
  period: BillingPeriod,
): number =>
  period === "ANNUAL" ? tier.annualPrice : tier.monthlyPrice * monthsIn(period);

/** Dirhams saved over a year by paying upfront rather than month by month. */
export const annualSavings = (tier: SubscriptionTier): number =>
  Math.max(0, tier.monthlyPrice * 12 - tier.annualPrice);

/** The same saving as a rounded percentage, for the discount badge. */
export const annualSavingsPercent = (tier: SubscriptionTier): number => {
  const fullYear = tier.monthlyPrice * 12;
  if (fullYear <= 0) return 0;
  return Math.round((annualSavings(tier) / fullYear) * 100);
};
